import React, { useState, useEffect } from 'react';
import { getApiClient } from '../../api';
import LoadingPage from '../../components/LoadingPage';
import { Navigate, useParams } from 'react-router-dom';
import { useMunicipalityId } from '../../hooks/useCouncilPath';
import { Dropdown, ProgressBar } from '../../components/PrimereactComponents';
import useDimensions from '../../hooks/useDimensions';
import PartyBarChart from '../../components/charts/PartyBarChart';
import { useTranslation } from 'react-i18next';
import { redirectErrorPage } from '../../utils/errors';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import MemberCardWrapper from '../../components/common/MemberCardWrapper/MemberCardWrapper';
import type { AttendanceOverview, PartyStats } from '../../api';
import './AttendanceAnalysis.css';
import { useAppContext } from '../../context/AppContext';
import { getColors } from '../../Constants';
import MemberCard from '../../components/common/MemberCard/MemberCard';
import { capitalizeFirstLetter, useFormatters } from '../../utils/texts';

function hexToHSL(hex: string) {
    // Remove '#' if present
    hex = hex.replace(/^#/, '');
    // Parse r, g, b
    let r = parseInt(hex.substring(0, 2), 16) / 255;
    let g = parseInt(hex.substring(2, 4), 16) / 255;
    let b = parseInt(hex.substring(4, 6), 16) / 255;

    let max = Math.max(r, g, b),
        min = Math.min(r, g, b);
    let h = 0,
        s = 0,
        l = (max + min) / 2;

    if (max !== min) {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h = h * 60;
    }
    return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

const getMembers = (
    basicData: AttendanceOverview | undefined,
    viewType: 'parties' | 'members'
): any[] => {
    if (basicData === undefined) {
        return [];
    }

    const members: any[] = [];
    basicData.data.forEach((party) => {
        party.clenove.forEach((member) => {
            let attendanceValue = member.dochazka;
            if (viewType === 'members' && typeof member.dochazka === 'string') {
                attendanceValue = parseFloat((member.dochazka as string).replace('%', ''));
            }

            members.push({
                id: member.id,
                firstName: member.jmeno,
                lastName: member.prijmeni,
                partyName: party.strana_zkratka,
                partyColor: party.strana_barva,
                gender: member.pohlavi || 0,
                dochazka: attendanceValue,
                aktivni: member.aktivni || (member.in_party !== undefined ? member.in_party : 1),
                photo: member.photo
            });
        });
    });

    members.sort((a, b) => {
        if (b.dochazka !== a.dochazka) {
            return b.dochazka - a.dochazka;
        }
        if (a.lastName !== b.lastName) {
            return a.lastName.localeCompare(b.lastName);
        }
        return a.firstName.localeCompare(b.firstName);
    });
    return members;
};

function AttendanceAnalysis() {
    const [loading, setLoading] = useState(true);
    const [basicData, setBasicData] = useState<AttendanceOverview | undefined>(undefined);
    const [error, setError] = useState<unknown>(null);
    const [partiesStats, setPartiesStats] = useState<PartyStats[] | undefined>(undefined);
    const { width } = useDimensions();
    const colors = getColors();
    const [selectedView, setSelectedView] = useState<'parties' | 'members'>('parties');

    const { t, i18n } = useTranslation(['analyses', 'translation']);

    const { bodyType = '', zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const { basicData: generalData } = useAppContext();
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);
    const members = getMembers(basicData, selectedView);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const api = getApiClient();
                const councilId = parseInt(zastupitelstvoId ?? '0', 10);
                const [attendanceData, statsData] = await Promise.all([
                    api.getAttendanceDashboardByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId, selectedView === 'members' ? { pohled: 'clenove' } : undefined),
                    api.getPartiesStatsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId)
                ]);

                setBasicData(attendanceData);
                setPartiesStats(statsData);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [bodyType, municipalitaId, zastupitelstvoId, selectedView]);

    if (loading) {
        return <LoadingPage />;
    }

    if (error) {
        redirectErrorPage(error);
        return null;
    }

    if (basicData !== undefined && generalData !== undefined && partiesStats !== undefined) {
        let filtered = partiesStats.filter((obj) => basicData.data.some((item) => item.strana_id === obj.strana_id));
        filtered.sort((a, b) => b.celkova_ucast - a.celkova_ucast);

        const attendanceItems = filtered.map((party) => ({
            label: party.strana_zkratka,
            value: party.celkova_ucast,
            color: party.strana_barva
        }));

        const handleAttendanceBarClick = (datum: { id: string | number }) => {
            const basicDataIndex = basicData.data.findIndex((party) => party.strana_zkratka === datum.id);

            setTimeout(() => {
                const element = document.getElementById(`${basicDataIndex}-datasetIndex-0`);
                if (element) {
                    setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'center' }), 330);
                }
            }, 0);
        };

        const adjustSaturationBasedOnAttendance = (attendance: number) => {
            const { h: baseH, s: baseS, l: baseL } = hexToHSL(getColors().primary);

            const adjustedS = attendance === 100 ? baseS : Math.max(10, (baseS * attendance) / 100);
            return `hsl(${baseH}, ${adjustedS}%, ${baseL}%)`;
        };

        const viewDropdown = (
            <div className='dropdown'>
                <h6>{t('attendance.view')}</h6>
                <Dropdown
                    inputId='select-view'
                    value={selectedView}
                    options={[
                        { name: t('attendance.viewByParty'), value: 'parties' },
                        { name: t('attendance.viewIndividualMembers'), value: 'members' }
                    ]}
                    onChange={(e) => setSelectedView(e.value)}
                    optionLabel='name'
                />
            </div>
        );

        return (
            <>
                <ContentSection title={t('attendance.analysis')} level={1} subtitle={t('attendance.analysisTooltip')} className='page-title-section' />
                <ContentSection
                    level={3}
                    title={t('attendance.byParty')}
                    subtitle={t('attendance.partiesTooltip')}
                    className='dashboard-card'
                >
                    <PartyBarChart
                        items={attendanceItems}
                        height={width < 768 ? '300px' : '450px'}
                        axisFormat={(value) => formatter.percentFrom100(Number(value))}
                        axisTitle={capitalizeFirstLetter(t('votingAttendance', { ns: 'translation' }))}
                        maxValue={100}
                        onBarClick={handleAttendanceBarClick}
                    />
                </ContentSection>
                <ContentSection
                    level={3}
                    title={t('attendance.councilMembers')}
                    subtitle={t('attendance.tooltip')}
                    className='attendance-analysis-members dashboard-card'
                    titleElement={viewDropdown}
                >
                    {selectedView === 'parties' && (
                        <>
                            {basicData?.data.map((party, i) => {
                                let memberGroupsData = [];
                                const activeMembers = party.clenove.filter((member) => member.in_party === 1);
                                const inactiveMembers = party.clenove.filter((member) => member.in_party === 0);

                                if (activeMembers.length > 0) {
                                    memberGroupsData.push({
                                        partyName: party.strana_zkratka,
                                        customCountText: t('activeCouncilMembers', { ns: 'members' }),
                                        partyColor: party.strana_barva,
                                        count: activeMembers.length,
                                        members: activeMembers.map((member) => {
                                            return {
                                                id: member.id,
                                                firstName: member.jmeno,
                                                lastName: member.prijmeni,
                                                inactive: member.in_party === 0,
                                                gender: member.pohlavi as 0 | 1, // API documents pohlavi as 0=male,1=female
                                                photo: member.photo,
                                                customCardContent: (
                                                    <ProgressBar
                                                        value={member.dochazka}
                                                        displayValueTemplate={(value : string | number) => formatter.percent2From100(value)}
                                                        color={adjustSaturationBasedOnAttendance(member.dochazka)}
                                                        style={{ height: '20px', width: '100%' }}
                                                    />
                                                    
                                                )
                                            };
                                        })
                                    });
                                }

                                if (inactiveMembers.length > 0) {
                                    memberGroupsData.push({
                                        partyName: party.strana_zkratka,
                                        customCountText: t('history', { ns: 'translation' }),
                                        partyColor: colors.darkGray,
                                        count: inactiveMembers.length,
                                        members: inactiveMembers.map((member) => {
                                            return {
                                                id: member.id,
                                                firstName: member.jmeno,
                                                lastName: member.prijmeni,
                                                gender: member.pohlavi as 0 | 1, // API documents pohlavi as 0=male,1=female
                                                photo: member.photo,
                                                customCardContent: (
                                                    <ProgressBar
                                                        value={member.dochazka}
                                                        displayValueTemplate={(value: string | number) => formatter.percent2From100(value)}
                                                        color={colors.darkGray}
                                                        style={{ height: '20px', width: '100%' }}
                                                    />
                                                )
                                            };
                                        })
                                    });
                                }

                                const relatedPartyStat = partiesStats.find(
                                    (partyStat) => partyStat.strana_id === party.strana_id
                                );

                                return (
                                    <MemberCardWrapper
                                        groupName={party.strana_zkratka}
                                        count={party.clenove.length}
                                        bgColor={party.strana_barva}
                                        partyId={party.strana_id}
                                        expanded
                                        memberGroupsData={memberGroupsData}
                                        key={i}
                                        tabIndex={i}
                                        extraDescriptionComponent={
                                            <h3 style={{ color: party.strana_barva }}>
                                                {formatter.percent2From100(relatedPartyStat?.celkova_ucast || 0)}
                                            </h3>
                                        }
                                    />
                                );
                            })}
                        </>
                    )}
                    {selectedView === 'members' && (
                        <>
                            <div className='member-card-list'>
                                {members
                                    .filter((member) => selectedView === 'members' || member.aktivni) // Filter active members only for 'members' view
                                    .map((member, index) => {
                                        if (!member.leader) {
                                            return (
                                                <MemberCard
                                                    key={index}
                                                    memberData={member}
                                                    partyData={{
                                                        partyName: member.partyName,
                                                        partyColor: member.partyColor
                                                    }}
                                                    customCardContent={
                                                        <ProgressBar
                                                            value={member.dochazka}
                                                            displayValueTemplate={(value: string | number) => formatter.percent2From100(value)}
                                                            color={adjustSaturationBasedOnAttendance(member.dochazka)}
                                                            style={{
                                                                height: '20px',
                                                                width: '100%'
                                                            }}
                                                        />
                                                    }
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                            </div>
                        </>
                    )}
                </ContentSection>
            </>
        );
    } else {
        return <Navigate to='/' />;
    }
}

export default AttendanceAnalysis;
